/* eslint-disable react/no-unknown-property */
import React from 'react';
import { Icon } from '@pypestream/design-system';
import {
  NodeColumns,
  NodeColumnsMap,
  tableStore,
} from '../../../stores/table-store';
import { getJsonOrderMap } from '../utils';
import { parseWhatsNext } from './parse-whats-next';

interface RoutingIntent {
  id: string;
  name: string;
  destination: string;
}

interface ParsedInputVars {
  data: Record<string, unknown>;
  names: string[];
}

const parseInputVars = (raw: string): ParsedInputVars | null => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const inputVars =
        typeof parsed.input_vars === 'string' ? parsed.input_vars : '';
      const names = inputVars
        .split(',')
        .map((n: string) => n.trim())
        .filter(Boolean);
      return { data: parsed, names };
    }
  } catch {
    // Not JSON — the caller falls back to the plain Raw JSON editor.
  }
  return null;
};

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return `routing-intent-${idCounter}`;
};

/**
 * Project Bobcat — "Manage Intents": a structured editor for a
 * `SysMultiMatchRouting` node's routing table.
 *
 * That table is really TWO columns acting as one concept: Parameter Input's
 * `input_vars` (the comma-separated intent names this node recognizes — real
 * shape confirmed against live customer CSVs, e.g.
 * `{"global_vars":"PERSISTENT_MENU_SELECTION","input_vars":"escalate,
 * mainmenu,endchat"}` in RBI.TimHortons) and `What Next?` (which node each
 * one routes to, `name~nodeNumber`, pipe-separated —
 * `mainmenu~99841|endchat~666|false~666|error~99990` on that same node).
 *
 * Both columns are written together via `tableStore.recordRowEdit` so one
 * add/rename/remove is ONE undo entry (PSTUDIO-2154) — the same convention
 * `node-command-select.tsx` and `node-type-cell.tsx` already use for a
 * change that touches more than one field. `committedIntentsRef` tracks the
 * intent names actually persisted right now (not just what's in local
 * state), so a RENAME correctly drops the old branch and adds the new one —
 * diffing `next` against local `intents` state instead would miss the old
 * name the moment `onChange` updates that state, since both would already
 * read the same post-rename value by commit time.
 *
 * A branch this node already routes that is NOT one of the managed intent
 * names (`error`, a `false` fallback, etc.) is preserved untouched — this
 * editor only ever adds, renames or removes entries whose label is a
 * managed intent.
 */
export const RoutingIntentsField: React.FC<{
  rowId: string;
  parameterInputRaw: string;
  disabled: boolean;
  onShowRawJson: () => void;
}> = ({ rowId, parameterInputRaw, disabled, onShowRawJson }) => {
  const parsed = parseInputVars(parameterInputRaw);

  const buildInitialIntents = React.useCallback((): RoutingIntent[] => {
    if (!parsed) return [];
    const whatsNextRaw = tableStore.cellValueForEditor(
      rowId,
      NodeColumns.whatsNext
    );
    const branches = parseWhatsNext(String(whatsNextRaw ?? ''));
    const branchByLabel = new Map(
      branches.map((b) => [b.label, b.destination])
    );
    return parsed.names.map((name) => ({
      id: nextId(),
      name,
      destination: branchByLabel.get(name) ?? '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId, parameterInputRaw]);

  const [intents, setIntents] =
    React.useState<RoutingIntent[]>(buildInitialIntents);
  const committedIntentsRef = React.useRef<RoutingIntent[]>(intents);

  // Reset to the row's real data on a row switch — the same rule
  // `ParameterInputField` follows for `activeSection` (`NodePropertyPanel`
  // keys fields by columnId alone, so this component instance survives a
  // row change and would otherwise keep showing the PREVIOUS row's intents).
  React.useEffect(() => {
    const initial = buildInitialIntents();
    setIntents(initial);
    committedIntentsRef.current = initial;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId]);

  if (!parsed) {
    return (
      <p className="c-node-property__help">
        This node&apos;s Parameter Input isn&apos;t a plain JSON object with an{' '}
        <code>input_vars</code> list —{' '}
        <button
          type="button"
          className="c-param-input__back"
          onClick={onShowRawJson}
        >
          edit the raw JSON instead
        </button>
        .
      </p>
    );
  }

  const commitIntents = (next: RoutingIntent[]) => {
    setIntents(next);

    const previousNames = committedIntentsRef.current.map((i) => i.name);
    const nextNames = next.map((i) => i.name);
    const managedNames = new Set([...previousNames, ...nextNames]);

    const whatsNextRaw = tableStore.cellValueForEditor(
      rowId,
      NodeColumns.whatsNext
    );
    const existingBranches = parseWhatsNext(String(whatsNextRaw ?? ''));
    const otherBranches = existingBranches.filter(
      (b) => !managedNames.has(b.label)
    );

    const nextWhatsNextArray = [
      ...otherBranches,
      ...next.map((i) => ({ label: i.name, destination: i.destination })),
    ]
      .filter((b) => b.label && b.destination)
      .map((b) => `${b.label}~${b.destination}`);

    const nextData = {
      ...parsed.data,
      input_vars: nextNames.filter(Boolean).join(','),
    };
    const nextParameterInput = JSON.stringify(nextData, null, 2);

    tableStore.recordRowEdit(
      rowId,
      'Edit routing intents',
      () => {
        tableStore.updateRow({
          rowId,
          updates: {
            // Stored as JSON text, as `saveParameterInput` writes it.
            [NodeColumns.parameterInput]:
              nextParameterInput as unknown as object,
            [NodeColumnsMap.parameterInputMap]: getJsonOrderMap({
              value: nextParameterInput,
              columnId: NodeColumns.parameterInput,
            }),
            [NodeColumns.whatsNext]: nextWhatsNextArray,
          },
          skipValidationRules: false,
          saveImmediately: true,
        });
      },
      NodeColumns.parameterInput
    );

    committedIntentsRef.current = next;
  };

  const updateIntent = (id: string, patch: Partial<RoutingIntent>) => {
    setIntents((current) =>
      current.map((intent) =>
        intent.id === id ? { ...intent, ...patch } : intent
      )
    );
  };

  const addIntent = () => {
    commitIntents([...intents, { id: nextId(), name: '', destination: '' }]);
  };

  const removeIntent = (id: string) => {
    commitIntents(intents.filter((intent) => intent.id !== id));
  };

  return (
    <div className="c-param-input__intents" test-id="ps-manage-intents">
      {intents.map((intent) => (
        <div className="c-param-input__intent-row" key={intent.id}>
          <input
            type="text"
            className="c-node-property__input"
            test-id="ps-manage-intents-name"
            placeholder="intent name"
            value={intent.name}
            disabled={disabled}
            onChange={(e) =>
              updateIntent(intent.id, { name: e.currentTarget.value })
            }
            onBlur={() => commitIntents(intents)}
          />
          <span className="c-param-input__intent-arrow">
            <Icon name="arrow-right" size="xsmall" />
          </span>
          <input
            type="text"
            className="c-node-property__input"
            test-id="ps-manage-intents-destination"
            placeholder="destination node #"
            value={intent.destination}
            disabled={disabled}
            onChange={(e) =>
              updateIntent(intent.id, { destination: e.currentTarget.value })
            }
            onBlur={() => commitIntents(intents)}
          />
          <button
            type="button"
            className="c-param-input__intent-remove"
            disabled={disabled}
            onClick={() => removeIntent(intent.id)}
            aria-label={`Remove ${intent.name || 'intent'}`}
          >
            <Icon name="close" size="xsmall" />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="c-param-input__back"
        disabled={disabled}
        onClick={addIntent}
      >
        + Add intent
      </button>
      <p className="c-node-property__help">
        Any other branch this node already routes (error, fallback) is kept
        as-is.{' '}
        <button
          type="button"
          className="c-param-input__back"
          onClick={onShowRawJson}
        >
          View raw JSON
        </button>{' '}
        instead.
      </p>
    </div>
  );
};
